/**
 * AnimatedModal - Reusable modal wrapper with smooth enter/exit animations
 *
 * Features:
 * - Fade in/out backdrop
 * - Slide up on enter, slide down on exit
 * - Handles exit animation before unmounting
 * - Click outside to close
 * - Escape key to close
 */

import React, { useEffect, useState, useCallback } from 'react';

interface AnimatedModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Additional classes for the modal panel */
  className?: string;
  /** Size preset: 'sm' | 'md' | 'lg' | 'xl' | 'full' */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Disable close on backdrop click */
  disableBackdropClose?: boolean;
  /** Disable close on escape key */
  disableEscapeClose?: boolean;
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
  full: 'inset-4 sm:inset-6 md:inset-8 lg:inset-12',
};

export const AnimatedModal: React.FC<AnimatedModalProps> = ({
  isOpen,
  onClose,
  children,
  className = '',
  size = 'md',
  disableBackdropClose = false,
  disableEscapeClose = false,
}) => {
  // Track mount state for animations
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle open
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Small delay to ensure the element is mounted before animating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    }
  }, [isOpen]);

  // Handle close when isOpen becomes false from parent
  useEffect(() => {
    if (!isOpen && isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible]);

  // Handle close with animation
  const handleClose = useCallback(() => {
    setIsAnimating(false);
    // Wait for animation to complete before unmounting
    setTimeout(() => {
      setIsVisible(false);
      onClose();
    }, 200); // Match the CSS transition duration
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || disableEscapeClose) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, disableEscapeClose, handleClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const isFullSize = size === 'full';

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-200 ease-out ${
          isAnimating ? 'opacity-50' : 'opacity-0'
        }`}
        onClick={disableBackdropClose ? undefined : handleClose}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div className={`absolute ${isFullSize ? 'inset-4 sm:inset-6 md:inset-8 lg:inset-12' : 'inset-0 flex items-center justify-center p-4'}`}>
        {/* Modal Panel */}
        <div
          className={`
            ${isFullSize ? 'w-full h-full' : `w-full ${sizeClasses[size]}`}
            bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden
            transition-all duration-200 ease-out
            ${isAnimating
              ? 'opacity-100 translate-y-0 scale-100'
              : 'opacity-0 translate-y-4 scale-[0.98]'
            }
            ${className}
          `}
          role="dialog"
          aria-modal="true"
        >
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * Simple hook for managing modal open/close state with animation support
 */
export const useModal = (initialState = false) => {
  const [isOpen, setIsOpen] = useState(initialState);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  return { isOpen, open, close, toggle, setIsOpen };
};

export default AnimatedModal;
